import { ACTIVE_RULESET_ID, type ReplayEnvelopeV1, type SectorTimesMs } from '@cars/shared';

export interface LapHistoryRowDto {
  replayId: string;
  carId: number;
  timeMs: number;
  sectorTimesMs: SectorTimesMs;
  visitedPitStop: boolean;
  pitStopCount?: number;
  completedAt: string;
}

export interface LapHistoryResponseDto {
  trackId: number;
  rulesetId: string;
  recordRevision: number;
  globalBestMs: number | null;
  personalBestMs: number | null;
  laps: LapHistoryRowDto[];
}

export interface ScoreDto {
  name: string;
  carId: number;
  time: number;
  replayId: string | null;
  verificationStatus: 'legacy' | 'verified';
  rulesetId: string;
  sectorTimesMs: SectorTimesMs | null;
}

export interface TimingReferenceLap {
  timeMs: number;
  sectorTimesMs: SectorTimesMs;
  replayId: string;
  carId: number;
}

export interface TimingReferencesDto {
  trackId: number;
  rulesetId: string;
  revision: number;
  globalRecord: TimingReferenceLap | null;
  personalBest: TimingReferenceLap | null;
}

export interface GlobalScoreDto extends ScoreDto {
  trackId: number;
}

interface ApiMessageResponse {
  message?: string;
  userId?: number;
  token?: string;
  expiresAt?: string;
}

export interface AuthResult {
  message: string;
  userId: number;
  token: string;
  expiresAt: string;
}

export interface RaceAttemptGrant {
  attemptId: string;
  challenge: string;
  issuedAt: string;
  expiresAt: string;

  replaySchemaVersion: number;
  tickRate: number;
  trackMapHash: string;
  carConfigHash: string;
  rulesetId: string;
  timingReferences: TimingReferencesDto;
}

export interface RaceAttemptSubmitResult {
  status: 'accepted' | 'rejected' | 'pending';
  code: string;
  message: string;
  personalBest?: boolean;
  replayId?: string;
  timeMs?: number;
  globalRecord?: boolean;
  rulesetId?: string;
  lap?: {
    timeMs: number;
    sectorTimesMs: SectorTimesMs;
    visitedPitStop?: boolean;
    pitStopCount?: number;
  };
  comparison?: {
    previousPersonalBestMs: number | null;
    previousGlobalBestMs: number | null;
    lapGapToPreviousGlobalMs: number | null;
  };
}

export class ApiError extends Error {
  readonly code: string | undefined;
  readonly status: number;

  constructor(message: string, code: string | undefined, status: number) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

let sessionToken: string | null = null;

export function getSessionToken(): string | null {
  return sessionToken;
}

export class ApiClient {
  private readonly baseUrl: string;

  constructor(baseUrl = '/api') {
    this.baseUrl = baseUrl;
  }

  async login(name: string, password: string): Promise<AuthResult> {
    return this.authenticate('login', name, password);
  }

  async register(name: string, password: string): Promise<AuthResult> {
    return this.authenticate('register', name, password);
  }

  async logout(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/logout`, {
      method: 'POST',
      headers: this.authHeaders(),
    });
    if (!response.ok && response.status !== 204) await this.readJson<unknown>(response);
    sessionToken = null;
  }

  async createRaceAttempt(trackId: number, carId: number): Promise<RaceAttemptGrant> {
    const response = await fetch(`${this.baseUrl}/race-attempts`, {
      method: 'POST',
      headers: this.authHeaders(),
      body: JSON.stringify({ trackId, carId }),
    });
    return this.readJson<RaceAttemptGrant>(response);
  }

  async submitRaceAttempt(
    attemptId: string,
    payload: {
      challenge: string;
      replay: ReplayEnvelopeV1;
      claimedTimeMs?: number;
      audit?: unknown;
      lapNumber?: number;
    },
  ): Promise<RaceAttemptSubmitResult> {
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(attemptId)
    ) {
      throw new Error('Invalid race attempt id');
    }
    const response = await fetch(
      `${this.baseUrl}/race-attempts/${encodeURIComponent(attemptId)}/submit`,
      {
        method: 'POST',
        headers: this.authHeaders(),
        body: JSON.stringify(payload),
      },
    );
    return this.readJson<RaceAttemptSubmitResult>(response);
  }

  async getScores(trackId: number): Promise<ScoreDto[]> {
    const params = new URLSearchParams({ track: String(trackId) });
    const url = `${this.baseUrl}/scores?${params.toString()}`;
    const response = await fetch(url);
    return this.readJson<ScoreDto[]>(response);
  }

  async getTimingReferences(trackId: number): Promise<TimingReferencesDto> {
    const params = new URLSearchParams({ track: String(trackId) });
    const response = await fetch(`${this.baseUrl}/timing-references?${params.toString()}`, {
      headers: this.authHeaders(),
    });
    return this.readJson<TimingReferencesDto>(response);
  }

  async getLapHistory(
    trackId: number,
    limit = 20,
    ruleset = ACTIVE_RULESET_ID,
    signal?: AbortSignal,
  ): Promise<LapHistoryResponseDto> {
    const params = new URLSearchParams({
      track: String(trackId),
      limit: String(Math.min(20, Math.max(1, Math.trunc(limit)))),
      ruleset,
    });
    const response = await fetch(`${this.baseUrl}/laps/history?${params.toString()}`, {
      headers: this.authHeaders(),
      signal,
    });
    return this.readJson<LapHistoryResponseDto>(response);
  }

  async getReplay(replayId: string): Promise<ReplayEnvelopeV1> {
    if (
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(replayId)
    ) {
      throw new Error('Invalid replay id');
    }
    const response = await fetch(`${this.baseUrl}/replays/${encodeURIComponent(replayId)}`);
    return this.readJson<ReplayEnvelopeV1>(response);
  }

  async getGlobalScores(trackIds: number[], limit = 20): Promise<GlobalScoreDto[]> {
    const perTrack = await Promise.all(
      trackIds.map(async (trackId) => {
        const rows = await this.getScores(trackId);
        return rows.map((row) => ({
          ...row,
          trackId,
        }));
      }),
    );

    return perTrack
      .flat()
      .sort((a, b) => a.time - b.time)
      .slice(0, limit);
  }

  private async authenticate(
    action: 'login' | 'register',
    name: string,
    password: string,
  ): Promise<AuthResult> {
    const response = await fetch(`${this.baseUrl}/${action}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, password }),
    });
    const result = await this.handleMessageResponse(response);
    if (
      typeof result.userId !== 'number' ||
      typeof result.token !== 'string' ||
      typeof result.expiresAt !== 'string'
    ) {
      throw new Error('The server returned an invalid authentication response');
    }

    sessionToken = result.token;
    return {
      message: result.message,
      userId: result.userId,
      token: result.token,
      expiresAt: result.expiresAt,
    };
  }

  private authHeaders(): HeadersInit {
    return {
      'content-type': 'application/json',
      ...(sessionToken ? { authorization: `Bearer ${sessionToken}` } : {}),
    };
  }

  private async handleMessageResponse(
    response: Response,
  ): Promise<ApiMessageResponse & { message: string }> {
    const payload = await this.readJson<ApiMessageResponse>(response);
    const message =
      typeof payload.message === 'string' ? payload.message : 'Unexpected server response';

    return {
      ...payload,
      message,
      userId: typeof payload.userId === 'number' ? payload.userId : undefined,
      token: typeof payload.token === 'string' ? payload.token : undefined,
      expiresAt: typeof payload.expiresAt === 'string' ? payload.expiresAt : undefined,
    };
  }

  private async readJson<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.toLowerCase().includes('application/json')) {
      throw new Error(
        `API returned ${contentType || 'an unknown content type'} (${response.status}). Check the development proxy and backend.`,
      );
    }

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new Error(`API returned invalid JSON (${response.status})`);
    }

    if (!response.ok) {
      const message =
        typeof payload === 'object' &&
        payload !== null &&
        'message' in payload &&
        typeof payload.message === 'string'
          ? payload.message
          : `API request failed (${response.status})`;
      const code =
        typeof payload === 'object' &&
        payload !== null &&
        'code' in payload &&
        typeof payload.code === 'string'
          ? payload.code
          : undefined;
      throw new ApiError(message, code, response.status);
    }

    return payload as T;
  }
}
